<template>
    <div v-if="visible" class="fixed inset-0 z-50 flex items-stretch justify-center bg-black/75 p-0 md:items-center md:p-4">
        <div class="flex h-full w-full max-w-5xl flex-col overflow-hidden border border-neutral-300 bg-white shadow-xl dark:border-neutral-700 dark:bg-neutral-950 md:max-h-[calc(100vh-2rem)] md:rounded-lg">
            <div class="flex shrink-0 items-start justify-between gap-4 border-b border-neutral-200 p-4 dark:border-neutral-800 md:p-5">
                <div class="min-w-0">
                    <div class="text-xs font-bold uppercase text-brand-primary-500">V3ME releases</div>
                    <h2 class="mt-1 text-2xl font-bold leading-tight text-neutral-950 dark:text-white">
                        What's new in V3ME
                    </h2>
                </div>
                <button
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded text-2xl leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                    type="button"
                    aria-label="Close changelog"
                    @click="handleClose"
                >
                    &times;
                </button>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto">
                <section v-if="heroRelease" class="border-b border-neutral-200 dark:border-neutral-800">
                    <div class="grid gap-0 lg:grid-cols-[1.3fr_.9fr]">
                        <div class="relative bg-black">
                            <video
                                ref="heroVideo"
                                class="aspect-[390/844] max-h-[58vh] w-full bg-black object-contain md:max-h-[680px]"
                                controls
                                playsinline
                                preload="metadata"
                                :poster="heroRelease.poster"
                                @ended="handleHeroVideoEnded"
                                @pause="heroVideoPlaying = false"
                                @play="handleHeroVideoStarted"
                                @timeupdate="handleHeroVideoProgress"
                            >
                                <source :src="heroRelease.videoUrl" type="video/mp4">
                            </video>
                            <button
                                v-if="!heroVideoPlaying"
                                class="absolute inset-0 flex items-center justify-center text-white"
                                type="button"
                                aria-label="Play release video"
                                @click="handleHeroVideoPlay"
                            >
                                <span class="flex h-24 w-24 items-center justify-center rounded-full bg-brand-primary-600 text-4xl text-white shadow-xl transition hover:bg-brand-primary-700 md:h-28 md:w-28">
                                    <Icon icon="play" pack="fas" class="ml-1"/>
                                </span>
                            </button>
                        </div>
                        <div class="flex flex-col justify-center p-5 md:p-7">
                            <div>
                                <div class="text-xs font-bold uppercase text-brand-primary-500">{{ heroRelease.version }}</div>
                                <h3 class="mt-2 text-2xl font-bold text-neutral-950 dark:text-white">
                                    {{ heroRelease.title }}
                                </h3>
                                <p class="mt-4 text-neutral-700 dark:text-neutral-300">
                                    {{ heroRelease.summary }}
                                </p>
                                <ul
                                    v-if="heroRelease.highlights && heroRelease.highlights.length"
                                    class="mt-5 space-y-2 text-sm text-neutral-700 dark:text-neutral-300"
                                >
                                    <li v-for="item in heroRelease.highlights" :key="item" class="flex gap-2">
                                        <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary-500"></span>
                                        <span>{{ item }}</span>
                                    </li>
                                </ul>
                                <div class="mt-6">
                                    <Button primary outlined size="sm" @click="handleReleaseFeedback(heroRelease)">
                                        Give Feedback
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="pastReleases.length" class="p-4 md:p-5">
                    <article
                        v-for="release in pastReleases"
                        :key="release.version"
                        class="border-t border-neutral-300 py-4 first:border-t-0 first:pt-0 dark:border-neutral-800"
                    >
                        <div>
                            <div class="text-xs font-bold uppercase text-brand-primary-500">{{ release.version }}</div>
                            <h3 class="mt-2 text-lg font-bold text-neutral-950 dark:text-white">
                                {{ release.title }}
                            </h3>
                            <p class="mt-3 text-sm text-neutral-700 dark:text-neutral-300">
                                {{ release.summary }}
                            </p>
                            <ul
                                v-if="release.highlights && release.highlights.length"
                                class="mt-3 space-y-2 text-sm text-neutral-700 dark:text-neutral-300"
                            >
                                <li v-for="item in release.highlights" :key="item" class="flex gap-2">
                                    <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary-500"></span>
                                    <span>{{ item }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="release.videoUrl" class="mt-3 text-sm">
                            <a
                                :href="release.videoUrl"
                                class="font-bold text-brand-primary-500"
                                @click="handleReleaseVideoLinkClick(release)"
                            >
                                Watch video
                            </a>
                        </div>
                    </article>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import {
  getLastSeenChangelogVersion,
  markChangelogReleaseSeen,
  releases,
} from '@/domain/Changelog/releases.js';
import {openFeatureFeedbackPrompt} from '@/utils/feedback.js';
import {capturePostHogEvent} from '@/utils/posthog.js';

export default {
  name: 'ChangelogModal',
  data() {
    return {
      changelogOpenedAt: null,
      changelogWasUnread: false,
      previousSeenVersion: null,
      videoCompleted: false,
      videoPlayed: false,
      videoProgressMilestones: [],
      heroVideoPlaying: false,
      visible: false,
    };
  },
  computed: {
    heroRelease() {
      return this.releases.find(release => release.videoUrl) || null;
    },
    pastReleases() {
      return this.releases.filter(release => release !== this.heroRelease);
    },
    releases() {
      return releases;
    },
  },
  methods: {
    capture(event, extra = {}) {
      capturePostHogEvent(event, this.eventProperties(extra));
    },
    eventProperties(extra = {}) {
      const video = this.$refs.heroVideo;
      const duration = video && Number.isFinite(video.duration) ? Math.round(video.duration) : null;
      const currentTime = video && Number.isFinite(video.currentTime) ? Math.round(video.currentTime) : null;
      const progressPercent = duration && currentTime !== null
        ? Math.min(100, Math.round((currentTime / duration) * 100))
        : null;

      return {
        release_count: this.releases.length,
        release_has_video: Boolean(this.heroRelease?.videoUrl),
        release_title: this.heroRelease?.title ?? null,
        release_version: this.heroRelease?.version ?? null,
        seconds_open: this.changelogOpenedAt ? Math.max(0, Math.round((Date.now() - this.changelogOpenedAt) / 1000)) : null,
        video_completed: this.videoCompleted,
        video_current_seconds: currentTime,
        video_duration_seconds: duration,
        video_played: this.videoPlayed,
        video_progress_percent: progressPercent,
        video_progress_milestone: this.videoProgressMilestones.length
          ? Math.max(...this.videoProgressMilestones)
          : null,
        was_unread: this.changelogWasUnread,
        previous_seen_version: this.previousSeenVersion,
        ...extra,
      };
    },
    handleHeroVideoPlay() {
      this.$refs.heroVideo?.play();
    },
    handleHeroVideoStarted() {
      this.heroVideoPlaying = true;

      if (this.videoPlayed) {
        return;
      }

      this.videoPlayed = true;
      this.capture('changelog_video_played');
    },
    handleHeroVideoProgress() {
      const video = this.$refs.heroVideo;

      if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      const progress = video.currentTime / video.duration;

      [25, 50, 75].forEach((milestone) => {
        if (progress < milestone / 100 || this.videoProgressMilestones.includes(milestone)) {
          return;
        }

        this.videoProgressMilestones.push(milestone);
        this.capture('changelog_video_progressed', {
          video_progress_milestone: milestone,
        });
      });
    },
    handleHeroVideoEnded() {
      this.heroVideoPlaying = false;

      if (this.videoCompleted) {
        return;
      }

      this.videoCompleted = true;
      this.capture('changelog_video_completed', {
        video_progress_milestone: 100,
        video_progress_percent: 100,
      });
    },
    handleReleaseVideoLinkClick(release) {
      this.capture('changelog_video_link_clicked', {
        linked_release_has_video: Boolean(release.videoUrl),
        linked_release_title: release.title,
        linked_release_version: release.version,
      });
    },
    handleReleaseFeedback(release) {
      this.capture('changelog_feedback_clicked', {
        feedback_release_title: release?.title ?? null,
        feedback_release_version: release?.version ?? null,
      });

      openFeatureFeedbackPrompt({
        context: {
          release_title: release?.title ?? null,
          release_version: release?.version ?? null,
        },
        promptLocation: 'changelog',
      });
    },
    handleClose() {
      this.capture('changelog_closed', {
        close_reason: 'close_button',
      });
      this.visible = false;
      this.$refs.heroVideo?.pause();
    },
    handleOpen() {
      if (this.visible) {
        return;
      }

      this.previousSeenVersion = getLastSeenChangelogVersion();
      this.changelogWasUnread = this.previousSeenVersion !== this.heroRelease?.version;
      this.changelogOpenedAt = Date.now();
      this.videoCompleted = false;
      this.videoPlayed = false;
      this.videoProgressMilestones = [];
      this.visible = true;
      this.heroVideoPlaying = false;
      this.capture('changelog_opened');
      markChangelogReleaseSeen();
    },
  },
  mounted() {
    window.addEventListener('v3me:changelog', this.handleOpen);
  },
  beforeUnmount() {
    window.removeEventListener('v3me:changelog', this.handleOpen);
  },
};
</script>
